import {
  For,
  Show,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onMount,
  type Component,
} from "solid-js";
import { api } from "../api";
import { bookingAnalytics } from "../analytics";
import {
  calendarCells as buildCalendarCells,
  formatBookingTime,
  formatDateHeading,
  formatMonthHeading,
  initialBookingMonth,
  monthKeyInTimezone,
  shiftMonthKey,
} from "../bookingTime";
import type {
  Availability,
  ManagedBooking,
  PublicEvent,
  TimeSlot,
} from "../types";
import Brand from "./Brand";
import {
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  GlobeIcon,
  VideoIcon,
} from "./icons";

interface Props {
  handle: string;
  slug: string;
}

interface Confirmation {
  booking_id: string;
  start: string;
  end: string;
  meet_url?: string;
  manage_url: string;
  rescheduled: boolean;
}

type Step = "calendar" | "details" | "confirmed";

const query = new URLSearchParams(window.location.search);

const PublicBooking: Component<Props> = (props) => {
  const rescheduleToken = query.get("reschedule") || undefined;
  const detectedTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const queryMonth = query.get("month");
  const [monthKey, setMonthKey] = createSignal(
    initialBookingMonth(queryMonth, detectedTimezone),
  );
  const [timezone, setTimezone] = createSignal(detectedTimezone);
  const [selectedDate, setSelectedDate] = createSignal<string>();
  const [selectedSlot, setSelectedSlot] = createSignal<TimeSlot>();
  const [step, setStep] = createSignal<Step>("calendar");
  const [name, setName] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [guests, setGuests] = createSignal<string[]>([]);
  const [note, setNote] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [formError, setFormError] = createSignal("");
  const [confirmation, setConfirmation] = createSignal<Confirmation>();

  const [event] = createResource(
    () => `${props.handle}/${props.slug}`,
    (path) => api<PublicEvent>(`/api/public/${path}`),
  );
  const [availability] = createResource(
    () => [props.handle, props.slug, monthKey(), timezone(), rescheduleToken] as const,
    ([handle, slug, currentMonth, zone, reschedule]) => {
      const params = new URLSearchParams({
        month: currentMonth,
        timezone: zone,
      });
      if (reschedule) params.set("reschedule", reschedule);
      return api<Availability>(
        `/api/public/${handle}/${slug}/availability?${params}`,
      );
    },
  );
  const [managed] = createResource(
    () => rescheduleToken,
    (token) => api<ManagedBooking>(`/api/manage/${token}`),
  );

  createEffect(() => {
    const value = managed();
    if (!value) return;
    setName(value.invitee_name);
    setEmail(value.invitee_email);
    setGuests(value.guest_emails);
    setNote(value.invitee_note);
    setTimezone(value.invitee_timezone);
    if (!queryMonth) {
      setMonthKey(
        monthKeyInTimezone(new Date(value.start), value.invitee_timezone),
      );
    }
  });

  const tracksMetripactConversions =
    props.handle.toLowerCase() === "metripact" && props.slug.toLowerCase() === "30min";

  onMount(() => {
    window.scrollTo(0, 0);
    if (tracksMetripactConversions) bookingAnalytics.initialize();
  });

  const slotsByDate = createMemo(() => {
    const result = new Map<string, TimeSlot[]>();
    for (const day of availability()?.days ?? []) result.set(day.date, day.slots);
    return result;
  });
  createEffect(() => {
    const requestedDate = query.get("date");
    if (
      requestedDate &&
      requestedDate.startsWith(monthKey()) &&
      slotsByDate().has(requestedDate) &&
      !selectedDate()
    ) {
      setSelectedDate(requestedDate);
    }
  });
  const selectedSlots = createMemo(
    () => slotsByDate().get(selectedDate() || "") ?? [],
  );
  const calendarCells = createMemo(() => {
    return buildCalendarCells(monthKey());
  });
  const timezoneOptions = createMemo(() => {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (key: "timeZone") => string[];
    };
    const values = intl.supportedValuesOf?.("timeZone") ?? [
      "UTC",
      "Europe/London",
      "Europe/Nicosia",
      "America/New_York",
      "America/Los_Angeles",
      "Asia/Dubai",
    ];
    return [
      ...new Set(
        [timezone(), event()?.profile.timezone, ...values].filter(
          (zone): zone is string => Boolean(zone),
        ),
      ),
    ];
  });

  function dateKey(day: number): string {
    return `${monthKey()}-${String(day).padStart(2, "0")}`;
  }

  function chooseDate(day: number): void {
    const key = dateKey(day);
    if (!slotsByDate().has(key)) return;
    setSelectedDate(key);
    setSelectedSlot(undefined);
  }

  function moveMonth(delta: number): void {
    setMonthKey((value) => shiftMonthKey(value, delta));
    setSelectedDate(undefined);
    setSelectedSlot(undefined);
  }

  function dateHeading(date = selectedDate()): string {
    return date ? formatDateHeading(date) : "";
  }

  function detailedWhen(slot = selectedSlot()): string {
    return slot
      ? formatBookingTime(slot.start, slot.end, timezone())
      : "";
  }

  function addGuest(): void {
    if (guests().length < 3) setGuests([...guests(), ""]);
  }

  function setGuest(index: number, value: string): void {
    setGuests((current) =>
      current.map((guest, currentIndex) =>
        currentIndex === index ? value : guest,
      ),
    );
  }

  async function submit(eventObject: SubmitEvent): Promise<void> {
    eventObject.preventDefault();
    const slot = selectedSlot();
    if (!slot) return;
    setSubmitting(true);
    setFormError("");
    try {
      const result = await api<Confirmation>(
        `/api/public/${props.handle}/${props.slug}/book`,
        {
          method: "POST",
          body: JSON.stringify({
            start: slot.start,
            timezone: timezone(),
            name: name(),
            email: email(),
            guests: guests().filter((guest) => guest.trim()),
            note: note(),
            reschedule_token: rescheduleToken,
          }),
        },
      );
      setConfirmation(result);
      setStep("confirmed");
      if (tracksMetripactConversions) {
        bookingAnalytics.trackBookingCompleted({
          bookingId: result.booking_id,
          rescheduled: result.rescheduled,
        });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : "Could not schedule the meeting.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      class="booking-page"
      style={{ "--brand": event()?.profile.brand_color ?? "#006bff" }}
    >
      <Show
        when={event()}
        fallback={
          <div class="page-state">
            <div class="loading-spinner" />
            <p>{event.loading ? "Loading scheduling page…" : event.error?.message}</p>
          </div>
        }
      >
        {(loaded) => (
          <section
            class="booking-card"
            classList={{
              "has-slots": Boolean(selectedDate()) && step() === "calendar",
              "details-mode": step() === "details",
              "confirmed-mode": step() === "confirmed",
            }}
          >
            <Show when={step() !== "confirmed"}>
              <aside class="booking-info">
                <Show when={step() === "details"}>
                  <button
                    class="round-back"
                    type="button"
                    aria-label="Back to date and time"
                    onClick={() => setStep("calendar")}
                  >
                    ←
                  </button>
                </Show>
                <div class="booking-brand">
                  <Brand
                    name={loaded().profile.brand_name}
                    logoUrl={loaded().profile.logo_url}
                    color={loaded().profile.brand_color}
                  />
                </div>
                <div class="booking-summary">
                  <p class="eyebrow">{loaded().profile.host_name}</p>
                  <h1>{loaded().event.title}</h1>
                  <Show when={loaded().event.description}>
                    <p class="event-description">{loaded().event.description}</p>
                  </Show>
                  <div class="summary-row">
                    <ClockIcon />
                    <strong>{loaded().event.duration_minutes} min</strong>
                  </div>
                  <div class="summary-row">
                    <VideoIcon />
                    <strong>Google Meet details provided upon confirmation.</strong>
                  </div>
                  <Show when={step() === "details" && selectedSlot()}>
                    <div class="summary-row">
                      <CalendarIcon />
                      <strong>{detailedWhen()}</strong>
                    </div>
                    <div class="summary-row">
                      <GlobeIcon />
                      <strong>{timezone().replaceAll("_", " ")}</strong>
                    </div>
                  </Show>
                </div>
                <div class="booking-legal">
                  <a href="/privacy">Privacy Policy</a>
                  <a href="/terms">Participant Terms</a>
                </div>
              </aside>
            </Show>

            <Show when={step() === "calendar"}>
              <div class="calendar-panel">
                <h2>Select a Date &amp; Time</h2>
                <div class="month-navigation">
                  <button
                    type="button"
                    onClick={() => moveMonth(-1)}
                    aria-label="Previous month"
                  >
                    ‹
                  </button>
                  <strong>
                    {formatMonthHeading(monthKey())}
                  </strong>
                  <button
                    type="button"
                    onClick={() => moveMonth(1)}
                    aria-label="Next month"
                  >
                    ›
                  </button>
                </div>
                <div class="calendar-grid" aria-label={monthKey()}>
                  <For each={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]}>
                    {(day) => <span class="weekday">{day}</span>}
                  </For>
                  <For each={calendarCells()}>
                    {(day) => (
                      <Show when={day} fallback={<span />}>
                        {(currentDay) => {
                          const key = () => dateKey(currentDay());
                          const available = () => slotsByDate().has(key());
                          return (
                            <button
                              class="calendar-day"
                              classList={{
                                available: available(),
                                selected: selectedDate() === key(),
                              }}
                              type="button"
                              disabled={!available()}
                              onClick={() => chooseDate(currentDay())}
                              aria-label={`${key()}${available() ? ", available" : ", unavailable"}`}
                            >
                              {currentDay()}
                            </button>
                          );
                        }}
                      </Show>
                    )}
                  </For>
                </div>
                <label class="timezone-field">
                  <span>Time zone</span>
                  <div>
                    <GlobeIcon />
                    <select
                      value={timezone()}
                      onChange={(eventObject) => {
                        setTimezone(eventObject.currentTarget.value);
                        setSelectedDate(undefined);
                        setSelectedSlot(undefined);
                      }}
                    >
                      <For each={timezoneOptions()}>
                        {(zone) => (
                          <option value={zone}>{zone.replaceAll("_", " ")}</option>
                        )}
                      </For>
                    </select>
                  </div>
                </label>
                <p class="timezone-explanation">
                  Times are shown in {timezone().replaceAll("_", " ")}.{" "}
                  {loaded().profile.host_name} sets availability in{" "}
                  {loaded().profile.timezone.replaceAll("_", " ")}.
                </p>
                <Show when={!availability.loading && !availability()?.calendar_connected}>
                  <p class="inline-alert">This host has not connected a calendar yet.</p>
                </Show>
              </div>

              <Show when={selectedDate()}>
                <aside class="slots-panel">
                  <button
                    type="button"
                    class="mobile-slots-back"
                    onClick={() => setSelectedDate(undefined)}
                  >
                    ←
                  </button>
                  <h3>{dateHeading()}</h3>
                  <p class="mobile-duration">
                    Duration: {loaded().event.duration_minutes} min
                  </p>
                  <div class="slot-list">
                    <For each={selectedSlots()}>
                      {(slot) => (
                        <div class="slot-choice">
                          <button
                            type="button"
                            class="slot-button"
                            classList={{ selected: selectedSlot()?.start === slot.start }}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {slot.label}
                          </button>
                          <Show when={selectedSlot()?.start === slot.start}>
                            <button
                              type="button"
                              class="next-button"
                              onClick={() => {
                                setStep("details");
                                window.scrollTo(0, 0);
                              }}
                            >
                              Next
                            </button>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </aside>
              </Show>
            </Show>

            <Show when={step() === "details"}>
              <section class="details-panel">
                <h2>Enter Details</h2>
                <form onSubmit={submit}>
                  <label>
                    Name <span aria-hidden="true">*</span>
                    <input
                      required
                      maxlength={120}
                      autocomplete="name"
                      value={name()}
                      onInput={(eventObject) => setName(eventObject.currentTarget.value)}
                    />
                  </label>
                  <label>
                    Email <span aria-hidden="true">*</span>
                    <input
                      required
                      type="email"
                      maxlength={254}
                      autocomplete="email"
                      value={email()}
                      onInput={(eventObject) => setEmail(eventObject.currentTarget.value)}
                    />
                  </label>
                  <For each={guests()}>
                    {(guest, index) => (
                      <label>
                        Guest {index() + 1}
                        <div class="guest-input">
                          <input
                            required
                            type="email"
                            maxlength={254}
                            value={guest}
                            onInput={(eventObject) =>
                              setGuest(index(), eventObject.currentTarget.value)
                            }
                          />
                          <button
                            type="button"
                            aria-label={`Remove guest ${index() + 1}`}
                            onClick={() =>
                              setGuests((current) =>
                                current.filter((_, currentIndex) => currentIndex !== index()),
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      </label>
                    )}
                  </For>
                  <Show when={guests().length < 3}>
                    <button class="add-guests" type="button" onClick={addGuest}>
                      Add guests
                    </button>
                  </Show>
                  <label>
                    {loaded().event.invitee_question}
                    <textarea
                      maxlength={5000}
                      rows={3}
                      value={note()}
                      onInput={(eventObject) => setNote(eventObject.currentTarget.value)}
                    />
                  </label>
                  <p class="participant-terms">
                    By proceeding, you confirm that you have read and agree to the{" "}
                    <a href="/terms" target="_blank">
                      Participant Terms
                    </a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank">
                      Privacy Notice
                    </a>
                    .
                  </p>
                  <Show when={formError()}>
                    <p class="form-error" role="alert">
                      {formError()}
                    </p>
                  </Show>
                  <button class="primary-button" type="submit" disabled={submitting()}>
                    {submitting()
                      ? "Scheduling…"
                      : rescheduleToken
                        ? "Reschedule Event"
                        : "Schedule Event"}
                  </button>
                </form>
              </section>
            </Show>

            <Show when={step() === "confirmed" && confirmation()}>
              {(confirmed) => (
                <section class="confirmation-panel">
                  <div class="success-icon">
                    <CheckIcon />
                  </div>
                  <p class="eyebrow">{loaded().profile.brand_name}</p>
                  <h1>{confirmed().rescheduled ? "Meeting rescheduled" : "You’re scheduled"}</h1>
                  <p>
                    A calendar invitation and confirmation email have been sent to
                    all participants.
                  </p>
                  <div class="confirmation-details">
                    <h2>{loaded().event.title}</h2>
                    <div class="summary-row">
                      <CalendarIcon />
                      <span>{detailedWhen({
                        start: confirmed().start,
                        end: confirmed().end,
                        label: "",
                      })}</span>
                    </div>
                    <div class="summary-row">
                      <GlobeIcon />
                      <span>{timezone().replaceAll("_", " ")}</span>
                    </div>
                  </div>
                  <Show when={confirmed().meet_url}>
                    <a class="primary-button link-button" href={confirmed().meet_url}>
                      Join Google Meet
                    </a>
                  </Show>
                  <a class="manage-link" href={confirmed().manage_url}>
                    Cancel or reschedule
                  </a>
                </section>
              )}
            </Show>
          </section>
        )}
      </Show>
    </main>
  );
};

export default PublicBooking;
